const PERIOD_NAMES = [
  'Midnight',    // 00–02
  'Early Hours', // 03–05
  'Dawn',        // 06–08
  'Morning',     // 09–11
  'Noon',        // 12–14
  'Afternoon',   // 15–17
  'Evening',     // 18–20
  'Night',       // 21–23
];

export function getPeriodName(hour) {
  return PERIOD_NAMES[Math.floor(hour / 3)];
}

export function formatTime(hour, minute) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

function advanceTimeByMinutes(time, minutes) {
  const total = (time.day - 1) * 1440 + time.hour * 60 + time.minute + minutes;
  const day = Math.floor(total / 1440) + 1;
  const rem = total % 1440;
  return { day, hour: Math.floor(rem / 60), minute: rem % 60 };
}

function regenEntity(entity, minutes) {
  const ticks = Math.floor(minutes / 10);
  if (ticks === 0) return entity;
  const gain = Math.floor(entity.maxHp * 0.05) * ticks;
  return { ...entity, hp: Math.min(entity.maxHp, entity.hp + gain) };
}

function regenCharacters(characters, minutes) {
  const ticks = Math.floor(minutes / 10);
  if (ticks === 0) return characters;
  const updated = {};
  for (const [id, c] of Object.entries(characters)) {
    updated[id] = regenEntity(c, minutes);
  }
  return { ...characters, ...updated };
}

export function createInitialRuntime(generatedCharacters = []) {
  const generatedMap = {};
  for (const c of generatedCharacters) {
    generatedMap[c.id] = c;
  }
  return {
    player: { name: 'Player', location: 'camp_fire', hp: 100, maxHp: 100 },
    characters: {
      sans:     { location: 'camp_fire', hp: 100, maxHp: 100, relationship: 0, awareness: 'unaware', flags: [], itinerary: [] },
      cat_girl: { location: 'camp_fire', hp: 80,  maxHp: 80,  relationship: 0, awareness: 'unaware', flags: [], itinerary: [] },
      ...generatedMap,
    },
    time: { day: 1, hour: 8, minute: 0 },
    log: ['You sit by the campfire. The flames crackle softly.'],
    combat: null,
    interactTarget: null,
  };
}

export function gameReducer(state, action) {
  switch (action.type) {
    case 'MOVE': {
      const { locationId, locationName, ttt } = action.payload;
      return {
        ...state,
        player: regenEntity({ ...state.player, location: locationId }, ttt),
        characters: regenCharacters(state.characters, ttt),
        time: advanceTimeByMinutes(state.time, ttt),
        interactTarget: null,
        log: [...state.log, `You move to ${locationName}.`],
      };
    }
    case 'SET_PLAYER_NAME': {
      return { ...state, player: { ...state.player, name: action.payload } };
    }
    case 'SET_CHARACTER_ITINERARY': {
      const { id, itinerary } = action.payload;
      if (!state.characters[id]) return state;
      return {
        ...state,
        characters: {
          ...state.characters,
          [id]: { ...state.characters[id], itinerary },
        },
      };
    }
    case 'SYNC_CHARACTER_LOCATIONS': {
      const { locations, messages = [] } = action.payload;
      const updates = {};
      for (const [id, location] of Object.entries(locations)) {
        if (state.characters[id] && state.characters[id].location !== location) {
          updates[id] = { ...state.characters[id], location };
        }
      }
      if (Object.keys(updates).length === 0 && messages.length === 0) return state;
      return {
        ...state,
        characters: { ...state.characters, ...updates },
        log: messages.length ? [...state.log, ...messages] : state.log,
      };
    }
    case 'SET_INTERACT_TARGET': {
      return { ...state, interactTarget: action.payload };
    }
    case 'START_COMBAT': {
      const { targetId, targetName } = action.payload;
      return {
        ...state,
        interactTarget: null,
        lastCombatResult: null,
        combat: { targetId, distance: 'mid', combatState: 'standing_off', turn: 'player' },
        log: [...state.log, `You engage ${targetName} in combat.`],
      };
    }
    case 'END_COMBAT': {
      const { message, result } = action.payload ?? {};
      return {
        ...state,
        combat: null,
        interactTarget: null,
        lastCombatResult: result ?? null,
        log: message ? [...state.log, message] : state.log,
      };
    }
    case 'COMBAT_ACTION': {
      void action.payload;
      const combat = state.combat;
      if (!combat) return state;

      const targetId = combat.targetId;
      const charRuntime = state.characters[targetId];
      const DAMAGE = 10;
      const newLog = [...state.log];

      // Player attacks
      const enemyHpAfter = Math.max(0, charRuntime.hp - DAMAGE);
      const playerLine = `You hit for ${DAMAGE} damage. (${charRuntime.hp} → ${enemyHpAfter})`;
      newLog.push(playerLine);

      if (enemyHpAfter <= 0) {
        return {
          ...state,
          characters: { ...state.characters, [targetId]: { ...charRuntime, hp: 0 } },
          combat: null,
          interactTarget: null,
          lastCombatResult: 'won',
          log: [...newLog, `Your opponent collapses.`],
        };
      }

      // Enemy attacks back
      const playerHpAfter = Math.max(0, state.player.hp - DAMAGE);
      const enemyLine = `They hit back for ${DAMAGE} damage. (${state.player.hp} → ${playerHpAfter})`;
      newLog.push(enemyLine);

      if (playerHpAfter <= 0) {
        return {
          ...state,
          player: { ...state.player, hp: 0 },
          characters: { ...state.characters, [targetId]: { ...charRuntime, hp: enemyHpAfter } },
          combat: null,
          interactTarget: null,
          lastCombatResult: 'ko',
          log: [...newLog, `You can't continue. You disengage.`],
        };
      }

      return {
        ...state,
        player: { ...state.player, hp: playerHpAfter },
        characters: { ...state.characters, [targetId]: { ...charRuntime, hp: enemyHpAfter } },
        combat: { ...combat, lastExchange: [playerLine, enemyLine] },
        log: newLog,
      };
    }
    case 'OBSERVE': {
      return { ...state, log: [...state.log, `You look at ${action.payload}.`] };
    }
    case 'ACTION': {
      switch (action.payload) {
        case 'rest': {
          const healed = Math.floor(state.player.maxHp * 0.3);
          const newHp = Math.min(state.player.maxHp, state.player.hp + healed);
          const gained = newHp - state.player.hp;
          const msg = gained > 0 ? `You rest for a while. (+${gained} HP)` : 'You rest for a while.';
          return { ...state, player: regenEntity({ ...state.player, hp: newHp }, 10), characters: regenCharacters(state.characters, 10), time: advanceTimeByMinutes(state.time, 10), log: [...state.log, msg] };
        }
        case 'look':
          return { ...state, log: [...state.log, 'You look around.'] };
        default:
          return state;
      }
    }
    default:
      return state;
  }
}
